import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useReveal } from "@/lib/useReveal";
import { Search, HeartPulse, LineChart, Handshake, Building2 } from "lucide-react";

const PILLARS = [
  {
    tag: "Find Customers",
    title: "Real people, real buying intent",
    body:
      "Type what you sell - Leadmato scans Reddit, forums, LinkedIn, YouTube and Instagram for the exact moments people are asking to buy, complaining about their current tool, or hunting for recommendations.",
    icon: <Search className="w-5 h-5" />,
    tone: "green",
    mock: (
      <div className="space-y-2 text-xs">
        <div className="rounded-lg bg-lm-surface border border-lm-border p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="font-mono text-lm-text2">X · SaaS founder</span>
            <span className="font-mono font-bold px-1.5 py-0.5 rounded bg-lm-tint2 text-lm-primary text-[10px]">92</span>
          </div>
          <div className="text-lm-text leading-snug">
            &ldquo;Anyone tried a CRM under ₹2k/user? Ours keeps crashing.&rdquo;
          </div>
          <span className="mt-1 inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-lm-tint2 text-lm-primary">
            buying
          </span>
        </div>
        <div className="rounded-lg bg-lm-surface border border-lm-border p-3">
          <div className="flex items-center justify-between mb-1">
            <span className="font-mono text-lm-text2">Reddit · r/CRM</span>
            <span className="font-mono font-bold px-1.5 py-0.5 rounded bg-lm-tint3 text-lm-secondary text-[10px]">84</span>
          </div>
          <div className="text-lm-text leading-snug">
            &ldquo;Salesforce is overkill for a 5-person team. Suggestions?&rdquo;
          </div>
          <span className="mt-1 inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-lm-tint3 text-lm-secondary">
            pain
          </span>
        </div>
      </div>
    ),
  },
  {
    tag: "Customer Pain Points",
    title: "Know what your audience actually struggles with",
    body:
      "Every scan clusters complaints and questions by theme, then ranks them by how often they show up, how severe they are, and how recent. Read the room before you build the pitch.",
    icon: <HeartPulse className="w-5 h-5" />,
    tone: "orange",
    mock: (
      <div className="space-y-2 text-xs">
        {[
          { theme: "White cast on darker skin tones", count: 47, severity: "high" },
          { theme: "Product breaks after one wash", count: 31, severity: "high" },
          { theme: "Delivery delays in Tier-2 cities", count: 22, severity: "med" },
          { theme: "Price bump post-launch offer", count: 14, severity: "low" },
        ].map((p) => (
          <div key={p.theme} className="rounded-lg bg-lm-surface border border-lm-border p-3 flex items-center justify-between gap-2">
            <span className="text-lm-text">{p.theme}</span>
            <span className="flex items-center gap-1.5 font-mono text-[10px]">
              <span className="text-lm-text3">×{p.count}</span>
              <span
                className={`px-1.5 py-0.5 rounded ${
                  p.severity === "high"
                    ? "bg-lm-tint3 text-lm-secondary"
                    : p.severity === "med"
                    ? "bg-lm-tint2 text-lm-primary"
                    : "bg-lm-tint text-lm-text2"
                }`}
              >
                {p.severity}
              </span>
            </span>
          </div>
        ))}
      </div>
    ),
  },
  {
    tag: "Market & Competitor Analysis",
    title: "One scan, the whole competitive map",
    body:
      "See where the conversation is happening about your competitors - share of voice, sentiment split, weakness heatmap by category, and the gaps where your product wins.",
    icon: <LineChart className="w-5 h-5" />,
    tone: "green",
    mock: (
      <div className="space-y-2.5">
        {[
          { name: "Competitor A", share: 34, sentiment: "mixed" },
          { name: "Competitor B", share: 26, sentiment: "positive" },
          { name: "Competitor C", share: 18, sentiment: "mixed" },
          { name: "Competitor D", share: 12, sentiment: "positive" },
          { name: "You (illustrative)", share: 4, sentiment: "positive" },
        ].map((c) => (
          <div key={c.name}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-lm-text2">{c.name}</span>
              <span className="font-mono text-lm-text3">
                {c.share}% · {c.sentiment}
              </span>
            </div>
            <div className="h-2 rounded-full bg-lm-tint overflow-hidden">
              <div
                className={`h-full ${c.name.startsWith("You") ? "bg-lm-secondary" : "bg-lm-primary"}`}
                style={{ width: `${c.share * 2.5}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    tag: "Partners & Influencers",
    title: "Find the people who move your niche",
    body:
      "Leadmato flips the lens - instead of buyers, it maps the high-influence authors, top communities, and creators who your customers already trust. Pitch a collab, not a cold DM.",
    icon: <Handshake className="w-5 h-5" />,
    tone: "orange",
    mock: (
      <div className="space-y-2 text-xs">
        {[
          { name: "r/IndianSkincare", type: "Subreddit", reach: "84k", tag: "community" },
          { name: "Skincare creator · Instagram", type: "Instagram", reach: "220k", tag: "creator" },
          { name: "Beauty reviewer · YouTube", type: "YouTube", reach: "1.1M", tag: "creator" },
          { name: "r/IndianSkincareAddicts", type: "Subreddit", reach: "32k", tag: "community" },
        ].map((p) => (
          <div key={p.name} className="rounded-lg bg-lm-surface border border-lm-border p-3 flex items-center justify-between">
            <div>
              <div className="font-mono text-lm-text">{p.name}</div>
              <div className="text-lm-text3 font-mono text-[10px]">{p.type} · reach {p.reach}</div>
            </div>
            <span
              className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${
                p.tag === "creator" ? "bg-lm-tint3 text-lm-secondary" : "bg-lm-tint2 text-lm-primary"
              }`}
            >
              {p.tag}
            </span>
          </div>
        ))}
      </div>
    ),
  },
  {
    tag: "Brand Foundation",
    title: "The expert lives inside the app",
    body:
      "Onboarding is a single input - your website. Leadmato reads it, builds your ICP, drafts your negative terms, seeds your keywords. You curate, you don't configure.",
    icon: <Building2 className="w-5 h-5" />,
    tone: "green",
    mock: (
      <div className="text-xs space-y-2">
        <div className="flex items-center gap-2 text-lm-primary font-mono">
          <span className="w-1.5 h-1.5 rounded-full bg-lm-primary animate-pulse" />
          Reading acmeskincare.in…
        </div>
        <div className="rounded-lg bg-lm-tint p-2.5 text-lm-text2 leading-relaxed">
          <div>
            <span className="text-lm-text3">Audience →</span> women 22-35, metro India, price band ₹500–₹1500
          </div>
          <div>
            <span className="text-lm-text3">Competitors →</span> auto-detected from your niche
          </div>
          <div>
            <span className="text-lm-text3">Keywords →</span> sunscreen white cast, SPF for oily skin, korean sunscreen india…
          </div>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">
          Confidence 94% · 3 fields need your eye
        </div>
      </div>
    ),
  },
];

export default function FeaturePillars() {
  const [headRef, headVisible] = useReveal();
  return (
    <section id="features" className="relative py-20 md:py-28 lm-cv">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <div
          ref={headRef}
          className={`max-w-4xl mx-auto text-center mb-14 lm-reveal ${headVisible ? "is-visible" : ""}`}
        >
          <div className="text-xs font-mono uppercase tracking-widest text-lm-secondary mb-3">Uncover every angle</div>
          <h2 className="text-3xl md:text-5xl font-display font-semibold leading-tight tracking-tight">
            Five lenses. One query. Zero dashboards to babysit.
          </h2>
          <p className="mt-4 text-lm-text2 leading-relaxed max-w-2xl mx-auto">
            Type what you sell - Leadmato hands back real people, real pain points, competitor gaps, potential partners
            and a brand foundation. No setup marathon, no weekly homework.
          </p>
        </div>

        <div className="space-y-6">
          {PILLARS.map((p, i) => (
            <Pillar key={p.title} pillar={p} idx={i} />
          ))}
        </div>

        <div className="mt-12 md:mt-14 flex justify-center">
          <Link
            to="/features"
            data-testid="pillars-see-all-features"
            className="group inline-flex items-center gap-2 bg-lm-primary hover:bg-lm-primaryHover text-white text-sm font-semibold px-6 py-3 rounded-lg transition-all shadow-[0_10px_26px_-12px_rgba(31,74,56,0.55)] hover:-translate-y-0.5"
          >
            See all features
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.2} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Pillar({ pillar, idx }) {
  const [ref, visible] = useReveal(0.1);
  const alignRight = idx % 2 === 1;
  return (
    <div
      ref={ref}
      className={`relative rounded-lg border border-lm-border bg-lm-surface overflow-hidden lm-reveal ${
        visible ? "is-visible" : ""
      }`}
    >
      <div
        className={`grid grid-cols-1 md:grid-cols-2 items-center gap-0 md:gap-6 ${
          alignRight ? "md:[direction:rtl]" : ""
        }`}
      >
        <div className={`p-8 md:p-12 ${alignRight ? "md:[direction:ltr]" : ""}`}>
          <div
            className={`inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest mb-4 ${
              pillar.tone === "orange" ? "text-lm-secondary" : "text-lm-primary"
            }`}
          >
            <span
              className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                pillar.tone === "orange" ? "bg-lm-tint3 text-lm-secondary" : "bg-lm-tint2 text-lm-primary"
              }`}
            >
              {pillar.icon}
            </span>
            {pillar.tag}
          </div>
          <h3 className="text-2xl md:text-3xl font-display font-semibold tracking-tight leading-tight">
            {pillar.title}
          </h3>
          <p className="mt-3 text-lm-text2 leading-relaxed">{pillar.body}</p>
        </div>
        <div
          className={`relative p-8 md:p-12 border-t md:border-t-0 md:border-l border-lm-border h-full flex items-center overflow-hidden ${
            alignRight ? "md:[direction:ltr]" : ""
          }`}
        >
          {/* Gradient illustration background */}
          <picture>
            <source srcSet="/pillar-gradient.webp" type="image/webp" />
            <img
              src="/pillar-gradient.jpg"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
              draggable="false"
              loading="lazy"
            />
          </picture>
          {/* Subtle cream overlay for content readability */}
          <div className="absolute inset-0 bg-lm-bg/25" />
          <div className="relative w-full rounded-lg bg-lm-surface border border-lm-border p-5 shadow-[0_18px_40px_-20px_rgba(31,74,56,0.35)]">
            {pillar.mock}
          </div>
        </div>
      </div>
    </div>
  );
}
